import UseContext from "../../../hooks/useContext";
import React, {useEffect, useState} from "react";
import DatePicker, {registerLocale} from "react-datepicker";
import {Calendar} from "../../../assets/icons";
import {enGB, fr} from "date-fns/locale";
import ScheduleWeekList from "../../../components/ScheduleWeekList/ScheduleWeekList";
import EmptyStateCompany from "../../../components/emptyState";
import {dateToEpoch} from "../../../helper/helper";
import SectionWrapper, {FormField, inputClassName} from "../components/SectionWrapper";

registerLocale('en-GB', enGB)
registerLocale('fr-FR', fr)

// Combined "Période" step for the template-driven mission flow:
// dates are captured here (location is owned by the template) and the
// resulting schedule is rendered below using the same primitives as before.
const Period = () => {
    const {setData, data, updSchedule, setUpdSchedule, setIsDateValid} = UseContext()
    const [scheduleWeeks, setScheduleWeeks] = useState([])
    const [scheduleWeekDisplay, setScheduleWeekDisplay] = useState(0)
    const [isScheduleChecked, setIsScheduleChecked] = useState(data.isScheduleCheck ? data.isScheduleCheck : false)
    const [dateError, setDateError] = useState(false)
    const [dateStartError, setDateStartError] = useState(false)

    useEffect(() => {
        const dday = dateToEpoch(new Date())
        if (data.dateStart && data.dateEnd) {
            const d0 = dateToEpoch(data.dateStart)
            const d1 = dateToEpoch(data.dateEnd)
            setDateError(d1 < d0)
            setDateStartError(dday > d0)
        }
    }, [data.dateStart, data.dateEnd])

    useEffect(() => {
        setIsDateValid(!dateError && !dateStartError)
    }, [dateError, dateStartError, setIsDateValid])

    const timeSheets = (start, end) => {
        const weeks = []
        let startDate = dateToEpoch(new Date(start))
        const endDate = dateToEpoch(new Date(end))
        let week = []
        while (startDate <= endDate) {
            week.push(dateToEpoch(startDate))
            if (startDate >= endDate || startDate.getDay() === 0) {
                weeks.push(week)
                week = []
            }
            startDate.setDate(startDate.getDate() + 1)
        }
        return weeks
    }

    useEffect(() => {
        if (data.schedule.length === 0) {
            if (data.dateStart && data.dateEnd) {
                const dateStartSchedule = dateToEpoch(new Date(data.dateStart))
                const dateEndSchedule = dateToEpoch(new Date(data.dateEnd))
                const weeksNew = timeSheets(dateStartSchedule, dateEndSchedule)
                const daysArrayNew = []
                for (let w in weeksNew) {
                    const daysNew = []
                    for (let i = 0; i < weeksNew[w].length; i++) {
                        daysNew.push({date: weeksNew[w][i], week: w})
                    }
                    daysArrayNew.push(daysNew)
                }
                const daysArrayNew2 = []
                const daysArrayCanBe2 = []
                for (let i = 0; i < daysArrayNew.length; i++) {
                    if (!daysArrayCanBe2.includes(i)) {
                        let isDaysArrayCanBe2 = false
                        if (daysArrayNew[i + 1] && daysArrayNew[i].length + daysArrayNew[i + 1].length <= 7) {
                            isDaysArrayCanBe2 = true
                            daysArrayCanBe2.push(i, i + 1)
                        }
                        if (isDaysArrayCanBe2) {
                            daysArrayNew2.push({
                                name: `Semaine ${('0' + (i + 1)).slice(-2)} & ${('0' + (i + 2)).slice(-2)}`,
                                days: [...daysArrayNew[i], ...daysArrayNew[i + 1]],
                            })
                        } else {
                            daysArrayNew2.push({name: `Semaine ${('0' + (i + 1)).slice(-2)}`, days: daysArrayNew[i]})
                        }
                    }
                }
                setScheduleWeeks(daysArrayNew2)
                data.schedule = daysArrayNew2
            }
        } else {
            setScheduleWeeks(data.schedule)
        }
        setUpdSchedule(false)
    }, [data, updSchedule])

    useEffect(() => {
        if (scheduleWeeks.length > 0) {
            setData({...data, schedule: scheduleWeeks})
        }
    }, [scheduleWeeks, updSchedule])

    useEffect(() => {
        setData({...data, isScheduleCheck: isScheduleChecked})
    }, [isScheduleChecked])

    const onDateStart = (date) => {
        if (dateToEpoch(data.dateStart) !== dateToEpoch(date)) {
            setData((prev) => ({...prev, schedule: [], isConfirmedSchedule: false}))
        }
        if (dateToEpoch(data.dateEnd) && dateToEpoch(date)) {
            const sameDay = dateToEpoch(data.dateEnd) === dateToEpoch(date)
            setData((prev) => ({...prev, isScheduleCheck: true, isScheduleRequired: sameDay}))
        }
        setData((prev) => ({...prev, dateStart: date}))
    }

    const onDateEnd = (date) => {
        if (dateToEpoch(data.dateEnd) !== dateToEpoch(date)) {
            setData((prev) => ({...prev, schedule: [], isConfirmedSchedule: false}))
        }
        if (dateToEpoch(data.dateStart) && dateToEpoch(date)) {
            const sameDay = dateToEpoch(data.dateStart) === dateToEpoch(date)
            setData((prev) => ({...prev, isScheduleCheck: true, isScheduleRequired: sameDay}))
        }
        setData((prev) => ({...prev, dateEnd: date}))
    }

    const switchElement = !data.isScheduleRequired ? (
        <button
            type="button"
            onClick={() => setIsScheduleChecked(!isScheduleChecked)}
            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                isScheduleChecked ? 'bg-yellow-500' : 'bg-gray-200'
            }`}
        >
            <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-sm transition-transform ${
                    isScheduleChecked ? 'translate-x-7' : 'translate-x-1'
                }`}
            />
        </button>
    ) : null

    return (
        <div className="flex flex-col gap-6">
            <SectionWrapper
                title="Période de la mission"
                description="Indiquez les dates de début et de fin de la mission."
            >
                <div className="grid grid-cols-2 gap-4">
                    <FormField label="Date de début">
                        <div className="relative">
                            <DatePicker
                                dateFormat="dd/MM/yyyy"
                                selected={data.dateStart}
                                onChange={onDateStart}
                                locale="fr-FR"
                                minDate={dateToEpoch(new Date())}
                                peekNextMonth
                                showMonthDropdown
                                showYearDropdown
                                disabledKeyboardNavigation
                                dropdownMode="select"
                                popperPlacement="bottom"
                                className={`${inputClassName} pl-8 cursor-pointer`}
                            />
                            <div className="absolute inset-y-0 left-0 flex items-center px-2 pointer-events-none">
                                <Calendar wh={18} color={'#666666'} strokeW={2} />
                            </div>
                        </div>
                        {dateStartError ? (
                            <span className="ft-sm text-red-500">Veuillez indiquer une date valide</span>
                        ) : null}
                    </FormField>
                    <FormField label="Date de fin">
                        <div className="relative">
                            <DatePicker
                                dateFormat="dd/MM/yyyy"
                                selected={data.dateEnd}
                                onChange={onDateEnd}
                                minDate={dateToEpoch(new Date(data.dateStart || new Date()))}
                                peekNextMonth
                                showMonthDropdown
                                showYearDropdown
                                disabledKeyboardNavigation
                                locale="fr-FR"
                                dropdownMode="select"
                                popperPlacement="bottom"
                                className={`${inputClassName} pl-8 cursor-pointer`}
                            />
                            <div className="absolute inset-y-0 left-0 flex items-center px-2 pointer-events-none">
                                <Calendar wh={18} color={'#666666'} strokeW={2} />
                            </div>
                        </div>
                        {dateError ? (
                            <span className="ft-sm text-red-500">Veuillez indiquer une date valide</span>
                        ) : null}
                    </FormField>
                </div>
            </SectionWrapper>
            <SectionWrapper
                title="Planning de la mission"
                description="Définissez les horaires de travail pour chaque jour de la mission."
                rightElement={switchElement}
            >
                {isScheduleChecked ? (
                    <div>
                        {scheduleWeeks?.map((scheduleWeek, index) => (
                            <ScheduleWeekList
                                key={index}
                                indexScheduleWeek={index}
                                scheduleWeek={scheduleWeek}
                                isPrevious={!!scheduleWeeks[index - 1]}
                                isNext={!!scheduleWeeks[index + 1]}
                                isHidden={scheduleWeekDisplay !== index}
                                setScheduleWeekDisplay={setScheduleWeekDisplay}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="my-10">
                        <EmptyStateCompany type="schedule" />
                    </div>
                )}
            </SectionWrapper>
        </div>
    )
}

export default Period
